import OrderActionsToolbar from "./payments/OrderActionsToolbar";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { elakayMoment } from "../../utils/elakayDate";
import { elakayAutoText, getElakayLanguageContext } from "./dataElakay";
import {
    FaBoxOpen, FaFilter, FaInbox, FaTimes, FaTruck, FaBarcode,
    FaShippingFast, FaClock, FaRoute, FaGlobeAmericas,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import {
    isAuthenticated,
    listOrders,
    sellerlistOrdersStatus,
} from "../auth";
import ButtonPagination from "../pageInside/ButtonPagination";
import CardOrders from '../pageInside/CardOrders2';
import OrderNoteMessage from '../pageInside/orderNoteMessage';
import OrdersView from '../pages/OrdersView';
import FiltersOrders from "../pageInside/filersOrder";
import TrackingSteps from "../pageInside/TrackingSteps";
import TrackingIframe from "../pageInside/TrackingIframe";
import { getOrderTrackingAPI } from "../auth/shipping";
import NoOrdersImage from '../Layout/imgs/no-ordres1.png';
import InfiniteScrollLoader from './InfiniteScrollLoader';
import { resolveInfiniteScroll } from '../../utils/infiniteScrollSettings';

// ✅ CSS partagé avec ProductGridPage (animations, skeletons)
import './ProductGridPage.css';
import './OrdersGridPage.css';

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["ordersGridPage"]?.[key] || fallback;

// ============================================
// DEFAULTS
// ============================================
export const DEFAULT_ORDER_FILTER = {
    orderStatus: 'none',
    orderDate:   'months',
    myd:         6,
    asc:         'desc',
};

// ============================================
// HELPERS
// ============================================
const useQuery = () => new URLSearchParams(useLocation().search);

const getRandomColor = () => {
    const colors = [
        'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
};

// An order can contain several product lines. The API therefore legitimately
// returns several rows with the same order `_id`; the nested order-product id
// is the stable identity of the rendered card.
const getOrderRowBaseKey = (orderItem, index) => {
    const orderId = orderItem?.orders_id || orderItem?._id;
    const productId =
        orderItem?.products?._id ||
        orderItem?.orderProductId ||
        orderItem?.products?.productID?._id ||
        orderItem?.products?.productID;
    const parts = [orderId, productId]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .map(String);
    return parts.length ? parts.join(':') : `order-row-${index}`;
};

// Order rows are identified by order + nested order-product. This keeps
// legitimate lines from the same order while preventing overlapping API
// pages from rendering the same line twice.
const mergeUniqueOrderRows = (current = [], incoming = []) => {
    const seen = new Set(current.map((row, index) => getOrderRowBaseKey(row, index)));
    const next = [...current];
    incoming.forEach((row, index) => {
        const key = getOrderRowBaseKey(row, index);
        if (seen.has(key)) return;
        seen.add(key);
        next.push(row);
    });
    return next;
};

// ============================================
// RESULT PAGINATION — header orders avec stats + filter
// ============================================
const OrdersResultPagination = ({
    skip             = 0,
    limit            = 3,
    size             = 0,
    sizeOrders       = 0,
    infoSource,
    style            = "minimal",
    customBackground = null,
    icon             = null,
    showIcon         = true,
    showBadge        = true,
    showPageInfo     = true,
    showFilterButton = true,
    onOpenFilter,
    animated         = false,
    className        = "",
    textColor        = null,
    textSize         = null,
    title,
    showTitle        = true,
}) => {
    __setElakayInfoSource(infoSource);
    const isSinglePage = skip === 0 && size < limit;
    const hasData      = size > 0;
    const realTotal    = isSinglePage ? size : sizeOrders;
    const start        = hasData ? skip + 1 : 0;
    const end          = isSinglePage ? size : Math.min(skip + limit, sizeOrders);
    const currentPage  = hasData ? Math.floor(skip / limit) + 1 : 0;
    const totalPages   = isSinglePage ? 1 : Math.ceil(sizeOrders / limit) || 0;

    const ofText         = infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.of         || "of";
    const ordersText     = infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.orders     || "orders";
    const totalOrderText = infoSource?.laguangeElakay?.('title', infoSource.languageAPI)?.totalOrder || "Total Orders";
    const filtersText    = infoSource?.language?.(infoSource, "title", "filters")                   || "Filters";

    const styleClass  = customBackground ? 'style-custom' : `style-${style}`;
    const customStyle = {
        ...(customBackground ? { background: customBackground } : {}),
        ...(textColor ? { color: textColor } : {}),
        ...(textSize ? { fontSize: textSize } : {}),
    };

    return (
        <div
            className={`pgp-result-bar ${styleClass} ${animated ? 'animated' : ''} ${className}`.trim()}
            style={customStyle}
        >
            <div className="pgp-result-left">
                {showIcon && (
                    <div className="pgp-result-icon">
                        {icon || <FaBoxOpen />}
                    </div>
                )}
                <div className="pgp-result-content">
                    {showTitle && <h5>
                        {title ?? totalOrderText}
                        {showBadge && hasData && (
                            <span className="pgp-result-badge">
                                <HiSparkles size={14} />
                                {realTotal} {ordersText}
                            </span>
                        )}
                    </h5>}
                    <p>
                        {hasData
                            ? `${start} - ${end} ${ofText} ${realTotal} ${ordersText}`
                            : `0 ${ordersText}`
                        }
                    </p>
                </div>
            </div>

            <div className="pgp-result-right ogp-result-right">
                {showFilterButton && onOpenFilter && (
                    <button
                        type="button"
                        className="ogp-filter-btn"
                        onClick={onOpenFilter}
                    >
                        <FaFilter />
                        <span>{filtersText}</span>
                    </button>
                )}
                {showPageInfo && hasData && (
                    <div className="pgp-page-info">{__elakayText("t008", "Page")}{currentPage} / {totalPages}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================
// ORDERS GRID — liste verticale
// ============================================
const OrdersGrid = ({
    orders         = [],
    infoSource,
    laguangeElakay,
    loadingTrakingOder,
    setTrackingOrder,
    setShowTrakingOrder,
    bntOderView,
    cardExtraProps = {},
    style            = "minimal",
    customBackground = null,
    containerClass   = 'container m-2 orders-card-container',
    emptyCount       = 4,
    animated         = false,
    animateCards     = false,
    className        = "",
    loading          = false,
    textColor        = null,
    textSize         = null,
}) => {
    const styleClass    = customBackground ? 'style-custom' : `style-${style}`;
    const animatedClass = animated ? 'animated' : '';
    const customStyle   = {
        ...(customBackground ? { background: customBackground } : {}),
        ...(textColor ? { color: textColor } : {}),
        ...(textSize ? { fontSize: textSize } : {}),
    };

    const renderOrders = () => {
        if (loading && orders.length === 0) {
            return Array.from({ length: emptyCount }).map((_, i) => (
                <div className="ogp-order-item-wrapper" key={`filler-${i}`}>
                    <div className="pgp-empty-card ogp-empty-order-card">
                        <div
                            className="pgp-empty-card-image ogp-empty-order-image"
                            style={{ background: getRandomColor() }}
                        />
                        <div className="pgp-empty-card-body">
                            <div className="pgp-skeleton title"></div>
                            <div className="pgp-skeleton subtitle"></div>
                            <div className="pgp-skeleton price"></div>
                        </div>
                    </div>
                </div>
            ));
        }

        const keyOccurrences = new Map();
        return orders.map((orderItem, i) => {
            const baseKey = getOrderRowBaseKey(orderItem, i);
            const occurrence = keyOccurrences.get(baseKey) || 0;
            keyOccurrences.set(baseKey, occurrence + 1);
            const rowKey = occurrence === 0 ? baseKey : `${baseKey}:duplicate-${occurrence}`;

            return (
                <div
                    className={`ogp-order-item-wrapper ${animateCards ? 'animate-appear' : ''}`}
                    key={rowKey}
                >
                    <div className={containerClass}>
                        <CardOrders
                            laguangeElakay={laguangeElakay}
                            infoSource={infoSource}
                            product={orderItem}
                            loadingTrakingOder={loadingTrakingOder}
                            setTrackingOrder={setTrackingOrder}
                            setShowTrakingOrder={setShowTrakingOrder}
                            bntOderView={bntOderView}
                            {...cardExtraProps}
                        />
                    </div>
                </div>
            );
        });
    };

    return (
        <div
            className={`pgp-grid-wrapper ogp-grid-wrapper ${styleClass} ${animatedClass} ${className}`.trim()}
            style={customStyle}
        >
            <div className="ogp-orders-stack">
                {renderOrders()}
            </div>
        </div>
    );
};

// ============================================
// ORDERS EMPTY STATE
// ============================================
const OrdersEmptyState = ({
    infoSource,
    showFilterButton = true,
    onOpenFilter,
}) => {
    const filtersText  = infoSource?.language?.(infoSource, "title", "filters")                      || "Filters";
    const noOrdersText = infoSource?.laguangeElakay?.('title', infoSource.languageAPI)?.noOrders    || "No orders yet";
    const noOrdersHelp = infoSource?.laguangeElakay?.('text',  infoSource.languageAPI)?.noOrdersMsg || "Your orders will appear here once you've made a purchase.";

    return (
        <div className="ogp-empty-state col-md-9">
            <div className="ogp-empty-state-inner">
                {showFilterButton && onOpenFilter && (
                    <button
                        type="button"
                        className="ogp-filter-btn ogp-filter-btn-float"
                        onClick={onOpenFilter}
                    >
                        <FaFilter />
                        <span>{filtersText}</span>
                    </button>
                )}

                <div className="ogp-empty-icon-wrapper">
                    <FaInbox className="ogp-empty-icon" />
                </div>

                <img
                    className="ogp-empty-image"
                    src={NoOrdersImage}
                    alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).ordersGridPage.t001}
                />

                <h3 className="ogp-empty-title">{noOrdersText}</h3>
                <p className="ogp-empty-message">{noOrdersHelp}</p>
            </div>
        </div>
    );
};

// ============================================
// ORDERS FILTER MODAL — dédié, avec FiltersOrders
// ============================================
const OrdersFilterModal = ({
    show,
    onHide,
    infoSource,
    laguangeElakay,
    filtOrders,
    setFiltOrders,
    limit,
    setLimit,
}) => {
    const titleText = infoSource?.language?.(infoSource, "title", "ordFilt") || "Orders Filters";

    return (
        <Modal
            size="md"
            show={!!show}
            onHide={onHide}
            aria-labelledby="filter-modal-title"
            centered
            dialogClassName="orders-filter-modal"
            backdrop="static"
        >
            <button
                type="button"
                className="orders-filter-modal-close"
                onClick={onHide}
                aria-label={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).ordersGridPage.t002}
            >×</button>
            <div className="orders-filter-modal-header">
                <h2 id="filter-modal-title" className="orders-filter-modal-title">
                    {titleText}
                </h2>
            </div>
            <div className="orders-filter-modal-body">
                <FiltersOrders
                    laguangeElakay={laguangeElakay}
                    infoSource={infoSource}
                    limit={parseInt(limit, 10)}
                    setLimit={setLimit}
                    setFiltOrders={setFiltOrders}
                    setShowFilterOrder={onHide}
                    filtOrders={filtOrders}
                />
            </div>
        </Modal>
    );
};

// ── "Just progressed" detection (task spec §9/§30: a small, one-shot
// animation only on a genuine transition, never on every refresh) ────────
// Purely a client-side UX nicety -- keyed on orderProductId, not
// security-sensitive, so localStorage (best-effort, never throws) is
// enough; no new backend field needed since the server already tells us
// the real state each time via GET .../tracking/order/....
const TRACKING_SEEN_STORAGE_PREFIX = "elakay:trackingSeen:";

const readSeenStepSignatures = (orderProductId) => {
    if (!orderProductId || typeof window === "undefined" || !window.localStorage) return null;
    try {
        const raw = window.localStorage.getItem(`${TRACKING_SEEN_STORAGE_PREFIX}${orderProductId}`);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeSeenStepSignatures = (orderProductId, signatures) => {
    if (!orderProductId || typeof window === "undefined" || !window.localStorage) return;
    try {
        window.localStorage.setItem(`${TRACKING_SEEN_STORAGE_PREFIX}${orderProductId}`, JSON.stringify(signatures));
    } catch {
        // Storage unavailable/full -- the animation is cosmetic, never worth failing the modal over.
    }
};

const stepSignature = (step) => `${step.key}:${step.state}`;

// Compares freshly-fetched steps against the last signatures this browser
// saw for this order item. Returns the set of step keys that genuinely
// moved to a new state since then -- an empty set on the very first view
// (nothing to compare against yet, so nothing is "new").
const detectJustChangedSteps = (orderProductId, steps) => {
    if (!Array.isArray(steps) || steps.length === 0) return new Set();
    const previous = readSeenStepSignatures(orderProductId);
    const nextSignatures = steps.map(stepSignature);
    writeSeenStepSignatures(orderProductId, nextSignatures);
    if (!Array.isArray(previous)) return new Set();
    const changed = new Set();
    steps.forEach((step, index) => {
        if (previous[index] !== nextSignatures[index]) changed.add(step.key);
    });
    return changed;
};

// ============================================
// ORDERS TRACKING MODAL
// ============================================
// Order Tracking Display task: one modal, conditional content, driven by
// AnimationSettings.trackingDisplaySettings.trackingDisplayMode
// (steps/iframe/both -- never hardcoded, task spec §4/§5). `trackingOrder`
// is `{ trackingUrl, orderId, orderProductId }` -- trackingUrl is the
// synchronous value CardOrders2.js already knew (kept as an instant
// fallback so the modal is never blank while the richer, server-normalized
// GET /shipping/tracking/order/:orderId/:orderProductId response is in
// flight or unavailable, task spec §19).
const OrdersTrackingModal = ({
    show,
    onHide,
    trackingOrder,
    infoSource,
    token,
}) => {
    const processingText = infoSource?.language?.(infoSource, "title", "orderprocess") || "Order is processing...";

    const fallbackTrackingUrl = (typeof trackingOrder === "string" ? trackingOrder : trackingOrder?.trackingUrl) || "";
    const orderId = trackingOrder?.orderId;
    const orderProductId = trackingOrder?.orderProductId;

    const configuredDisplayMode = infoSource?.allSettings?.trackingDisplaySettings?.trackingDisplayMode || "steps";

    const [trackingData, setTrackingData] = useState(null);
    const [loadingTracking, setLoadingTracking] = useState(false);
    const [activeTab, setActiveTab] = useState("progress");
    const [justChangedKeys, setJustChangedKeys] = useState(() => new Set());

    useEffect(() => {
        if (!show) return undefined;
        setActiveTab("progress");
        setTrackingData(null);
        setJustChangedKeys(new Set());
        if (!orderId || !orderProductId) return undefined;

        let cancelled = false;
        setLoadingTracking(true);
        getOrderTrackingAPI(token, orderId, orderProductId)
            .then((data) => {
                if (cancelled || !data || data.error) return;
                setTrackingData(data);
                setJustChangedKeys(detectJustChangedSteps(orderProductId, data.steps));
            })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setLoadingTracking(false); });

        return () => { cancelled = true; };
    }, [show, orderId, orderProductId, token]);

    const displayMode = trackingData?.displayMode || configuredDisplayMode;
    const resolvedTrackingUrl = trackingData?.trackingUrl || fallbackTrackingUrl;
    // Before the server response lands, still attempt the iframe (matches
    // today's behavior) -- once it lands, trust its explicit allowlist check.
    const iframeAllowed = trackingData ? Boolean(trackingData.iframeAllowed) : Boolean(fallbackTrackingUrl);

    const renderHeader = () => {
        if (!trackingData) return null;
        const rows = [
            [FaTruck, elakayAutoText("h_trk_carrier", "Carrier"), trackingData.carrier],
            [FaBarcode, elakayAutoText("h_trk_number", "Tracking Number"), trackingData.trackingNumber],
            [FaShippingFast, elakayAutoText("h_trk_provider", "Provider"), trackingData.provider],
            [
                FaClock,
                elakayAutoText("h_trk_last_updated", "Last Updated"),
                trackingData.lastUpdated ? elakayMoment(trackingData.lastUpdated).format("lll") : null
            ]
        ].filter(([, , value]) => value);
        if (rows.length === 0) return null;
        return (
            <div className="orders-tracking-modal-header">
                {rows.map(([Icon, label, value]) => (
                    <div className="orders-tracking-modal-header-row" key={label}>
                        <Icon className="orders-tracking-modal-header-icon" aria-hidden="true" />
                        <span className="orders-tracking-modal-header-label">{label}</span>
                        <span className="orders-tracking-modal-header-value">{value}</span>
                    </div>
                ))}
            </div>
        );
    };

    const renderTabs = () => (
        <div className="tracking-tabs" role="tablist">
            <button
                type="button"
                role="tab"
                aria-selected={activeTab === "progress"}
                className={`tracking-tab ${activeTab === "progress" ? "active" : ""}`}
                onClick={() => setActiveTab("progress")}
            >
                <FaRoute aria-hidden="true" />
                {elakayAutoText("h_trk_tab_progress", "Order Progress")}
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={activeTab === "carrier"}
                className={`tracking-tab ${activeTab === "carrier" ? "active" : ""}`}
                onClick={() => setActiveTab("carrier")}
            >
                <FaGlobeAmericas aria-hidden="true" />
                {elakayAutoText("h_trk_tab_carrier", "Carrier Tracking")}
            </button>
        </div>
    );

    const renderBody = () => {
        // Defensive fallback: no order/item ids at all (shouldn't happen
        // from CardOrders2.js, but never crash the Orders page over it,
        // task spec §19) -- behave like the pre-existing iframe-only modal.
        if (!orderId || !orderProductId) {
            return (
                <TrackingIframe
                    trackingUrl={fallbackTrackingUrl}
                    iframeAllowed={Boolean(fallbackTrackingUrl)}
                    infoSource={infoSource}
                />
            );
        }
        if (loadingTracking && !trackingData && !fallbackTrackingUrl) {
            return <h2 className="text-center m-3">{processingText}</h2>;
        }
        if (displayMode === "iframe") {
            return (
                <TrackingIframe
                    trackingUrl={resolvedTrackingUrl}
                    iframeAllowed={iframeAllowed}
                    infoSource={infoSource}
                />
            );
        }
        if (displayMode === "both") {
            return (
                <div className="tracking-both-view">
                    {renderTabs()}
                    <div className="tracking-tab-panel">
                        {activeTab === "progress" ? (
                            <TrackingSteps
                                steps={trackingData?.steps}
                                exception={trackingData?.exception}
                                justChangedKeys={justChangedKeys}
                                infoSource={infoSource}
                            />
                        ) : (
                            <TrackingIframe
                                trackingUrl={resolvedTrackingUrl}
                                iframeAllowed={iframeAllowed}
                                infoSource={infoSource}
                            />
                        )}
                    </div>
                </div>
            );
        }
        // "steps" (default -- task spec §3: trackingDisplayMode defaults to "steps")
        return (
            <TrackingSteps
                steps={trackingData?.steps}
                exception={trackingData?.exception}
                justChangedKeys={justChangedKeys}
                infoSource={infoSource}
            />
        );
    };

    return (
        <Modal
            show={!!show}
            onHide={onHide}
            centered
            dialogClassName="orders-tracking-modal"
            aria-labelledby="tracking-modal-title"
            backdrop="static"
        >

            <button
                type="button"
                className="orders-tracking-modal-close"
                onClick={onHide}
                aria-label={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).ordersGridPage.t003}
            ><FaTimes aria-hidden="true" /></button>
            {renderHeader()}
            {renderBody()}
        </Modal>
    );
};

// ============================================
// ORDERS ITEM VIEW MODAL (avec note tabs)
// ============================================
// const OrdersItemModal = ({
//     show,
//     onHide,
//     infoSource,
//     token,
//     getOrderID,

//     showOrderNote,
//     setShowOrderNote,

//     pageImgSms,
//     setPageImgSms,
//     graphicNote,
//     setGraphicNote,
//     graphicNoteBs,
//     setGraphicNoteBs,
//     graphicImgBs,
//     setGraphicImgBs,
// }) => {
//     // ── Tabs Message/Images ──────────────────────────────────────────
//     const stepPageMessage = () => {
//         const StyleMenu0 = {
//             borderLeft:      "1px solid lightgrey",
//             borderRight:     "1px solid lightgrey",
//             borderBottom:    "None",
//             backgroundColor: "White"
//         };
//         const StyleMenu1 = {
//             borderLeft:      "1px solid lightgrey",
//             borderRight:     "1px solid lightgrey",
//             backgroundColor: "White",
//             borderBottom:    "6px solid #007399"
//         };
//         const plsh1 = pageImgSms  ? StyleMenu1 : StyleMenu0;
//         const plsh2 = !pageImgSms ? StyleMenu1 : StyleMenu0;

//         return (
//             <div className="row justify-content-center">
//                 <div className="col-xs-12">
//                     <div className="col-12 col-md-12" style={{ textAlign: "center" }}>
//                         <div className="row">
//                             <div
//                                 className="col col-6 orders-step-tab"
//                                 style={plsh2}
//                                 onClick={() => setPageImgSms?.(false)}
//                                 role="button"
//                                 tabIndex={0}
//                             >Message</div>
//                             <div
//                                 className="col-6 orders-step-tab"
//                                 style={plsh1}
//                                 onClick={() => setPageImgSms?.(true)}
//                                 role="button"
//                                 tabIndex={0}
//                             >Images</div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     // ── Modal note (imbriqué dans item modal) ────────────────────────
//     const renderNoteModal = () => (
//         <Modal
//             size="md"
//             show={!!showOrderNote}
//             onHide={() => setShowOrderNote?.(false)}
//             dialogClassName="orders-note-modal"
//             aria-labelledby="order-note-modal-title"
//             centered
//             backdrop="static"
//         >
//             <Modal.Header closeButton>
//                 <Modal.Title id="order-note-modal-title">
//                     {stepPageMessage()}
//                 </Modal.Title>
//             </Modal.Header>
//             <Modal.Body>
//                 <OrderNoteMessage
//                     orderID={getOrderID?.oid}
//                     token={token}
//                     pageImgSms={pageImgSms}
//                     infoSource={infoSource}
//                     setGraphicNote={setGraphicNote}
//                     graphicNote={graphicNote}
//                     graphicNoteBs={graphicNoteBs}
//                     setGraphicNoteBs={setGraphicNoteBs}
//                     graphicImgBs={graphicImgBs}
//                 />
//             </Modal.Body>
//         </Modal>
//     );
//     const ordersViewModal  = () => (
//           <Modal
//             size="xl"
//             show={!!show}
//             onHide={onHide}
//             dialogClassName="orders-item-modal"
//             aria-labelledby="order-items-modal-title"
//             centered
//             backdrop="static"
//         >
//             <button
//                 type="button"
//                 className="orders-item-modal-close"
//                 onClick={onHide}
//                 aria-label="Close"
//             >×</button>
//             <div className="orders-item-modal-body">
                
//                 <OrdersView
//                     infoSource={infoSource}
//                     getOrderID={getOrderID}
//                     setShowOrderNote={setShowOrderNote}
//                     setGraphicNoteBs={setGraphicNoteBs}
//                     setGraphicImgBs={setGraphicImgBs}
//                 />
//             </div>
//         </Modal>

//     )

//     return (
//     <>
// {renderNoteModal()}
// {ordersViewModal()}
// </>
//     );
// };

// ============================================
// ORDERS ITEM VIEW MODAL — FULLSCREEN
// ============================================
//
// Remplace la version précédente dans OrdersGridPage.jsx
// Le modal prend tout l'écran pour bien afficher OrdersView
// (product card + payment + shipping + form return/cancel)
//
// Changements:
//   - fullscreen={true} au lieu de size="xl" + centered
//   - classe .orders-item-modal-fullscreen ajoutée
//   - le note modal imbriqué reste size="md" centered (pas fullscreen)
//   - bouton close absolute top-right avec z-index élevé
// ============================================

const OrdersItemModal = ({
    show,
    onHide,
    infoSource,
    token,
    getOrderID,

    showOrderNote,
    setShowOrderNote,

    pageImgSms,
    setPageImgSms,
    graphicNote,
    setGraphicNote,
    graphicNoteBs,
    setGraphicNoteBs,
    graphicImgBs,
    setGraphicImgBs,
}) => {
    // ── Tabs Message/Images ──────────────────────────────────────────
    const stepPageMessage = () => {
        const StyleMenu0 = {
            borderLeft:      "1px solid lightgrey",
            borderRight:     "1px solid lightgrey",
            borderBottom:    "None",
            backgroundColor: "White"
        };
        const StyleMenu1 = {
            borderLeft:      "1px solid lightgrey",
            borderRight:     "1px solid lightgrey",
            backgroundColor: "White",
            borderBottom:    "6px solid #007399"
        };
        const plsh1 = pageImgSms  ? StyleMenu1 : StyleMenu0;
        const plsh2 = !pageImgSms ? StyleMenu1 : StyleMenu0;

        return (
            <div className="row justify-content-center">
                <div className="col-xs-12">
                    <div className="col-12 col-md-12" style={{ textAlign: "center" }}>
                        <div className="row">
                            <div
                                className="col col-6 orders-step-tab"
                                style={plsh2}
                                onClick={() => setPageImgSms?.(false)}
                                role="button"
                                tabIndex={0}
                            >{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).ordersGridPage.t005}</div>
                            <div
                                className="col-6 orders-step-tab"
                                style={plsh1}
                                onClick={() => setPageImgSms?.(true)}
                                role="button"
                                tabIndex={0}
                            >{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).ordersGridPage.t006}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ── Note Modal (imbriqué, reste normal) ──────────────────────────
    const renderNoteModal = () => (
        <Modal
            size="md"
            show={!!showOrderNote}
            onHide={() => setShowOrderNote?.(false)}
            dialogClassName="orders-note-modal"
            aria-labelledby="order-note-modal-title"
            centered
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title id="order-note-modal-title">
                    {stepPageMessage()}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <OrderNoteMessage
                    orderID={getOrderID?.oid}
                    token={token}
                    pageImgSms={pageImgSms}
                    infoSource={infoSource}
                    setGraphicNote={setGraphicNote}
                    graphicNote={graphicNote}
                    graphicNoteBs={graphicNoteBs}
                    setGraphicNoteBs={setGraphicNoteBs}
                    graphicImgBs={graphicImgBs}
                />
            </Modal.Body>
        </Modal>
    );

    return (
        <Modal
            show={!!show}
            onHide={onHide}
            size="xl"
            fullscreen={true}
            dialogClassName="orders-item-modal orders-item-modal-fullscreen"
            aria-labelledby="order-items-modal-title"
            backdrop="static"
        >
            <button
                type="button"
                className="orders-item-modal-close"
                onClick={onHide}
                aria-label={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).ordersGridPage.t007}
                style={{
                    position: 'absolute',
                    top: '12px',
                    right: '16px',
                    zIndex: 1100,
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '2px solid #007399',
                    color: '#007399',
                    borderRadius: '50%',
                    width: '44px',
                    height: '44px',
                    fontSize: '1.75rem',
                    lineHeight: '1',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >×</button>

            <div
                className="orders-item-modal-body"
                style={{
                    height: '100vh',
                    overflowY: 'auto',
                    padding: '1.5rem 1rem',
                    background: '#f8fafc',
                }}
            >
                {renderNoteModal()}
                <OrdersView
                    infoSource={infoSource}
                    getOrderID={getOrderID}
                    setShowOrderNote={setShowOrderNote}
                    setGraphicNoteBs={setGraphicNoteBs}
                    setGraphicImgBs={setGraphicImgBs}
                />
            </div>
        </Modal>
    );
};

// ============================================
// MAIN COMPONENT — OrdersGridPage
// ============================================
//
// ARCHITECTURE:
//   - filtOrders géré en interne via useState(defaultFilter)
//   - Parent peut fournir defaultFilter custom (seller: myd=1, customer: myd=6)
//   - loaderFn configurable: listOrders (customer) | sellerlistOrdersStatus (seller)
//   - Self-contained: tous les modals + leur state gérés en interne
//   - Support mode controlled pour DynamicPageRenderer
//
// ┌──────────────────────────────────────────────────────────────────────┐
// │ MODE 1 — AUTONOME CUSTOMER (remplace Orders.jsx v5.2 legacy logic)  │
// │                                                                      │
// │   <OrdersGridPage                                                    │
// │     infoSource={infoSource}                                          │
// │     laguangeElakay={laguangeElakay}                                  │
// │     defaultFilter={{ orderStatus: 'none', orderDate: 'months', myd: 6 }}│
// │   />                                                                 │
// │                                                                      │
// │   → Utilise listOrders par défaut                                    │
// │   → Gère filtOrders, modals, pagination en interne                   │
// └──────────────────────────────────────────────────────────────────────┘
//
// ┌──────────────────────────────────────────────────────────────────────┐
// │ MODE 2 — AUTONOME SELLER                                            │
// │                                                                      │
// │   import { sellerlistOrdersStatus } from '../auth';                  │
// │   <OrdersGridPage                                                    │
// │     infoSource={infoSource}                                          │
// │     loaderFn={sellerlistOrdersStatus}                                │
// │     defaultFilter={{ orderStatus: 'none', orderDate: 'months', myd: 1 }}│
// │     defaultLimit={5}                                                 │
// │   />                                                                 │
// └──────────────────────────────────────────────────────────────────────┘
//
// ┌──────────────────────────────────────────────────────────────────────┐
// │ MODE 3 — CONTROLLED (via SECTIONS_LIBRARY.ordersGrid)               │
// │                                                                      │
// │   <OrdersGridPage                                                    │
// │     controlled={true}                                                │
// │     orders={pageData.orders}                                         │
// │     sizeOrders={pageData.ordersCount}                                │
// │     skip={paginationProps.skip}                                      │
// │     loadMore={paginationProps.loadMore}                              │
// │     onFiltersChange={(filters) => reloadFacets(['ordersGrid'])}     │
// │   />                                                                 │
// │                                                                      │
// │   → filtOrders toujours géré en interne                              │
// │   → Les changements de filtres notifient le parent via callback      │
// │   → Pagination contrôlée par le parent                               │
// └──────────────────────────────────────────────────────────────────────┘

const OrdersGridPage = ({
    // ── Mode toggle ─────────────────────────────────────────────────
    controlled         = false,

    // ── Display props ───────────────────────────────────────────────
    infoSource,
    laguangeElakay,

    // ── Result bar ──────────────────────────────────────────────────
    // Valeurs par défaut lues depuis AnimationSettings
    // (infoSource.allSettings.productGrids.ordersGridPage) si non fournies
    // explicitement par l'appelant — voir effective* plus bas.
    resultStyle        = "minimal",
    resultBackground,
    resultIcon         = null,
    showResultIcon     = true,
    showResultBadge    = true,
    showResultPageInfo = true,
    showFilterButton   = true,
    resultAnimated,

    // ── Grid wrapper ────────────────────────────────────────────────
    gridStyle          = "minimal",
    gridBackground,
    gridAnimated,
    animateCards,
    containerClass     = 'container m-2 orders-card-container',
    cardExtraProps     = {},                  // props extras à passer à CardOrders (ex: bnt: true)

    containerStyle     = {},
    containerClassName = "mt-3 mb-3",
    emptyCount         = 4,
    backgroundColor    = "WhiteSmoke",

    // ── Filters config ──────────────────────────────────────────────
    defaultFilter      = DEFAULT_ORDER_FILTER,
    defaultLimit       = 3,
    onFiltersChange,                          // callback optionnel quand filtres changent

    // ── Loader config ───────────────────────────────────────────────
    loaderFn           = listOrders,          // listOrders | sellerlistOrdersStatus | autre
    token:    tokenProp = null,
    sortBy             = '',

    // ── Query param handling ────────────────────────────────────────
    handleQueryParams  = true,                // si true, ouvre modal item depuis ?oid=...

    // ── bntOderView override ────────────────────────────────────────
    onOrderSelect,                            // (oid, pid, opid) => void — override par défaut

    // ── Controlled mode props ───────────────────────────────────────
    orders:     controlledOrders,
    sizeOrders: controlledSizeOrders,
    size:       controlledSize,
    skip:       controlledSkip,
    limit:      controlledLimit,
    loadMore:   controlledLoadMore,
    loadMore0:  controlledLoadMore0,
    page                = 'ORDERS',
    sectionId           = 'ordersGrid',
}) => {

    // Apparence configurable depuis AnimationSettings (page "Pages produits &
    // commandes"). Une prop explicite passée par l'appelant reste prioritaire.
    const gridSettings = infoSource?.allSettings?.productGrids?.ordersGridPage || {};
    const effectiveResultBackground = resultBackground ?? gridSettings.resultBackground ?? null;
    const effectiveResultAnimated   = resultAnimated   ?? gridSettings.resultAnimated   ?? false;
    const effectiveGridBackground   = gridBackground   ?? gridSettings.gridBackground   ?? null;
    const effectiveGridAnimated     = gridAnimated     ?? gridSettings.gridAnimated     ?? false;
    const effectiveAnimateCards     = animateCards     ?? gridSettings.animateCards     ?? false;

    // ═══════════════════════════════════════════════════════════════
    // TOKEN
    // ═══════════════════════════════════════════════════════════════
    const { token: authToken } = isAuthenticated();
    const token = tokenProp || authToken;

    // ═══════════════════════════════════════════════════════════════
    // QUERY PARAMS (?oid=...)
    // ═══════════════════════════════════════════════════════════════
    const query    = useQuery();
    const location = useLocation();
    const navigate = useNavigate();

    // ═══════════════════════════════════════════════════════════════
    // STATE INTERNE — FILTRES (toujours, même en mode controlled)
    // ═══════════════════════════════════════════════════════════════
    const [filtOrders, setFiltOrdersInternal] = useState(defaultFilter);
    // Page size — admin-configurable via AnimationSettings → General →
    // "Pagination & Limites" (productGrids.ordersGridPage.limit). Falls
    // back to defaultLimit (3) exactly as before when unset.
    const [limit,      setLimit]              = useState(() => {
        const configured = Number(gridSettings.limit);
        return Number.isFinite(configured) && configured > 0 ? configured : defaultLimit;
    });

    // Wrapper qui notifie le parent si callback fourni
    const setFiltOrders = useCallback((next) => {
        setFiltOrdersInternal((prev) => {
            const resolved = typeof next === 'function' ? next(prev) : next;
            onFiltersChange?.(resolved);
            return resolved;
        });
    }, [onFiltersChange]);

    // ═══════════════════════════════════════════════════════════════
    // STATE INTERNE — ORDERS (mode autonome uniquement)
    // ═══════════════════════════════════════════════════════════════
    const [skip,       setSkip]       = useState(0);
    const [orders,     setOrders]     = useState([]);
    const [sizeOrders, setSizeOrders] = useState(0);
    const [loading,    setLoading]    = useState(false);
    const [loadError,  setLoadError]  = useState(null);
    const requestVersionRef = useRef(0);
    const loadingRequestRef = useRef(false);
    const mountedRef = useRef(true);

    const infiniteConfig = resolveInfiniteScroll(infoSource?.allSettings, page, sectionId);
    const infiniteEnabled = infiniteConfig.enabled;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            requestVersionRef.current += 1;
        };
    }, []);

    // ═══════════════════════════════════════════════════════════════
    // STATE INTERNE — MODALS
    // ═══════════════════════════════════════════════════════════════
    const [showFilterOrder,  setShowFilterOrder]  = useState(false);
    const [showTrakingOrder, setShowTrakingOrder] = useState(false);
    const [showOrderNote,    setShowOrderNote]    = useState(false); 
    const [showItermOrder,   setShowItermOrder]   = useState(false);
   

    const [trackingOrder,    setTrackingOrder]    = useState('');
    const [getOrderID,       setGetOrderID]       = useState({ oid: '', pid: '', opid: '' });
    const [pageImgSms,       setPageImgSms]       = useState(false);
    const [graphicNote,      setGraphicNote]      = useState('');
    const [graphicNoteBs,    setGraphicNoteBs]    = useState([]);
    const [graphicImgBs,     setGraphicImgBs]     = useState([]);

    const divOrdersGrid = useRef(null);

    const scrollToParagraph = () => {
        divOrdersGrid.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // ═══════════════════════════════════════════════════════════════
    // OPEN ITEM MODAL (via CardOrders click ou query params)
    // ═══════════════════════════════════════════════════════════════
    const bntOderView = useCallback((oid, pid, opid) => {
        if (onOrderSelect) {
            onOrderSelect(oid, pid, opid);
            return;
        }
        setGetOrderID({ oid, pid, opid });
        setShowItermOrder(true);
    }, [onOrderSelect]);

    // ═══════════════════════════════════════════════════════════════
    // QUERY PARAMS — ouvrir modal depuis URL, puis nettoyer
    // ═══════════════════════════════════════════════════════════════
    useEffect(() => {
        if (!handleQueryParams) return;

        const oid = query.get('oid');
        if (!oid) return;

        setGetOrderID({
            oid,
            pid:  query.get('pid')  || '',
            opid: query.get('opid') || '',
        });
        setShowItermOrder(true);

        // Nettoyer l'URL pour éviter réouverture au remount
        const params = new URLSearchParams(location.search);
        params.delete('oid');
        params.delete('pid');
        params.delete('opid');
        const clean = params.toString();
        navigate(
            `${location.pathname}${clean ? `?${clean}` : ''}`,
            { replace: true }
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleQueryParams, location.search]);

    // ═══════════════════════════════════════════════════════════════
    // MODE AUTONOME — API fetch
    // ═══════════════════════════════════════════════════════════════
    const fetchOrders = useCallback((toSkip = 0, { append = false } = {}) => {
        if (!token) {
            return;
        }
        if (typeof loaderFn !== 'function') {
            console.error('[OrdersGridPage] loaderFn is not a function');
            return;
        }

        if (loadingRequestRef.current) return;
        const requestVersion = ++requestVersionRef.current;
        loadingRequestRef.current = true;
        setLoading(true);
        setLoadError(null);
        loaderFn(token, toSkip, parseInt(limit, 10), sortBy, filtOrders)
            .then(response => {
                if (!mountedRef.current || requestVersion !== requestVersionRef.current) return;
                if (response?.error) {
                    setLoadError(response.error);
                    return;
                }
                const nextOrders = response.orders || [];
                setOrders((current) => append ? mergeUniqueOrderRows(current, nextOrders) : nextOrders);
                setSizeOrders(response.totalCount?.[0]?.count || 0);
                setSkip(toSkip);
            })
            .catch(() => {
                if (mountedRef.current && requestVersion === requestVersionRef.current) {
                    setLoadError(__elakayText('t_load_error', 'Unable to load more orders.'));
                }
            })
            .finally(() => {
                if (mountedRef.current && requestVersion === requestVersionRef.current) {
                    loadingRequestRef.current = false;
                    setLoading(false);
                }
            });
    }, [token, limit, filtOrders, loaderFn, sortBy]);

    // ── Init + re-fetch quand filtOrders/limit/token changent ───────
    useEffect(() => {
        if (controlled) return;
        requestVersionRef.current += 1;
        loadingRequestRef.current = false;
        setOrders([]);
        setSkip(0);
        fetchOrders(0, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled, token, limit, JSON.stringify(filtOrders)]);

    // ═══════════════════════════════════════════════════════════════
    // PAGINATION HANDLERS
    // ═══════════════════════════════════════════════════════════════
    const handleLoadMore = useCallback(() => {
        if (controlled) {
            controlledLoadMore?.();
            return;
        }
        const toSkip = skip + parseInt(limit, 10);
        if (!infiniteEnabled) scrollToParagraph();
        fetchOrders(toSkip, { append: infiniteEnabled });
    }, [controlled, controlledLoadMore, skip, limit, fetchOrders, infiniteEnabled]);

    const handleLoadMore0 = useCallback(() => {
        if (controlled) {
            controlledLoadMore0?.();
            return;
        }
        const toSkip = Math.max(0, skip - parseInt(limit, 10));
        scrollToParagraph();
        fetchOrders(toSkip);
    }, [controlled, controlledLoadMore0, skip, limit, fetchOrders]);

    // ═══════════════════════════════════════════════════════════════
    // RESOLUTION — valeurs finales selon mode
    // ═══════════════════════════════════════════════════════════════
    const finalOrders     = controlled ? (controlledOrders     || []) : orders;
    const finalSize       = controlled ? (controlledSize       ?? finalOrders.length) : orders.length;
    const finalSizeOrders = controlled ? (controlledSizeOrders ?? 0)  : sizeOrders;
    const finalSkip       = controlled ? (controlledSkip       ?? 0)  : skip;
    const finalLimit      = controlled ? (controlledLimit      ?? limit) : limit;
    const hasMore         = finalOrders.length < finalSizeOrders;
    const titleMode       = infiniteConfig.titleMode || 'existing';

    const isEmpty = !loading && finalOrders.length === 0;

    // ═══════════════════════════════════════════════════════════════
    // HANDLERS MODAL
    // ═══════════════════════════════════════════════════════════════
    const openFilterModal  = useCallback(() => setShowFilterOrder(true), []);
    const closeFilterModal = useCallback(() => setShowFilterOrder(false), []);

    const closeTrackingModal = useCallback(() => setShowTrakingOrder(false), []);
    const closeItemModal     = useCallback(() => setShowItermOrder(false), []);

    // ═══════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════
    return (
        <>
            {(cardExtraProps.bnt === true || loaderFn === sellerlistOrdersStatus) && <OrderActionsToolbar onUpdated={() => controlled ? onFiltersChange?.({...filtOrders}) : fetchOrders(0)} />}
            <div
                ref={divOrdersGrid}
                className={`row my-4 justify-content-center orders-premium-page ${containerClassName}`}
                style={{ backgroundColor, ...containerStyle }}
            >
                {isEmpty ? (
                    <OrdersEmptyState
                        infoSource={infoSource}
                        showFilterButton={showFilterButton}
                        onOpenFilter={openFilterModal}
                    />
                ) : (
                    <>
                        <OrdersResultPagination
                            skip={infiniteEnabled ? 0 : finalSkip}
                            limit={infiniteEnabled ? Math.max(finalOrders.length, finalLimit) : finalLimit}
                            size={finalSize}
                            sizeOrders={finalSizeOrders}
                            infoSource={infoSource}
                            style={resultStyle}
                            customBackground={effectiveResultBackground}
                            icon={resultIcon}
                            showIcon={showResultIcon}
                            showBadge={showResultBadge}
                            showPageInfo={showResultPageInfo && !infiniteEnabled}
                            showFilterButton={showFilterButton}
                            onOpenFilter={openFilterModal}
                            animated={effectiveResultAnimated}
                            textColor={gridSettings.resultTextColor}
                            textSize={gridSettings.resultTextSize}
                            title={titleMode === 'custom' ? infiniteConfig.customTitle : undefined}
                            showTitle={titleMode !== 'hidden'}
                        />

                        <OrdersGrid
                            orders={finalOrders}
                            infoSource={infoSource}
                            laguangeElakay={laguangeElakay}
                            setTrackingOrder={setTrackingOrder}
                            setShowTrakingOrder={setShowTrakingOrder}
                            bntOderView={bntOderView}
                            cardExtraProps={cardExtraProps}
                            style={gridStyle}
                            customBackground={effectiveGridBackground}
                            containerClass={containerClass}
                            emptyCount={emptyCount}
                            animated={effectiveGridAnimated}
                            textColor={gridSettings.gridTextColor}
                            textSize={gridSettings.gridTextSize}
                            animateCards={effectiveAnimateCards}
                            loading={loading}
                        />

                        {finalOrders.length > 0 && !infiniteEnabled && (
                            <div 
                            // className="ogp-pagination-wrapper"
                            >
                                <ButtonPagination
                                    infoSource={infoSource}
                                    size={finalSize}
                                    skip={finalSkip}
                                    limit={parseInt(finalLimit, 10)}
                                    siseProduct={finalSizeOrders}
                                    loadMore={handleLoadMore}
                                    loadMore0={handleLoadMore0}
                                />
                            </div>
                        )}
                        {infiniteEnabled && (
                            <InfiniteScrollLoader
                                enabled
                                hasMore={hasMore}
                                loading={loading}
                                error={loadError}
                                onLoadMore={handleLoadMore}
                                onRetry={handleLoadMore}
                                skeleton={finalOrders.length > 0 ? (
                                    <OrdersGrid
                                        orders={[]}
                                        infoSource={infoSource}
                                        laguangeElakay={laguangeElakay}
                                        loading
                                        emptyCount={emptyCount}
                                    />
                                ) : null}
                            />
                        )}
                    </>
                )}
            </div>

            {/* ═══ MODALS ═══ */}

            <OrdersFilterModal
                show={showFilterOrder}
                onHide={closeFilterModal}
                infoSource={infoSource}
                laguangeElakay={laguangeElakay}
                filtOrders={filtOrders}
                setFiltOrders={setFiltOrders}
                limit={limit}
                setLimit={setLimit}
            />

            <OrdersTrackingModal
                show={showTrakingOrder}
                onHide={closeTrackingModal}
                trackingOrder={trackingOrder}
                infoSource={infoSource}
                token={token}
            />

            <OrdersItemModal
                show={showItermOrder}
                onHide={closeItemModal}
                infoSource={infoSource}
                token={token}
                getOrderID={getOrderID}
                showOrderNote={showOrderNote}
                setShowOrderNote={setShowOrderNote}
                pageImgSms={pageImgSms}
                setPageImgSms={setPageImgSms}
                graphicNote={graphicNote}
                setGraphicNote={setGraphicNote}
                graphicNoteBs={graphicNoteBs}
                setGraphicNoteBs={setGraphicNoteBs}
                graphicImgBs={graphicImgBs}
                setGraphicImgBs={setGraphicImgBs}
            />
        </>
    );
};

export {
    OrdersResultPagination,
    OrdersGrid,
    OrdersEmptyState,
    OrdersFilterModal,
    OrdersTrackingModal,
    OrdersItemModal,
    mergeUniqueOrderRows,
    useQuery,
};
export default OrdersGridPage;
